/**
 * Sharing things out, which is the other half of division.
 *
 * The Divide toy asks "how many 17s fit inside 2443" — grouping. This asks
 * "share 17 between 5 of you" — partitioning. They are the same arithmetic and
 * they do not feel the same at all, and the second one is where a remainder
 * finally makes sense: the two left over are not an abstraction, they are the
 * sweets nobody can have without cutting one in half.
 */

export interface Share {
  total: number;
  plates: number;
  /** How many each plate ends up with. */
  each: number;
  /** What could not be shared without breaking one. */
  left: number;
}

export function shareOut(total: number, plates: number): Share {
  const n = Math.max(0, Math.floor(total));
  const p = Math.max(1, Math.floor(plates));
  return { total: n, plates: p, each: Math.floor(n / p), left: n % p };
}

/**
 * Which plate the item at `index` lands on, dealing one to each plate in turn
 * the way a person actually deals cards — or null when it is a leftover.
 *
 * Dealing round-robin rather than filling one plate at a time is the whole
 * picture: it is why the leftovers are the ones that could not complete a lap.
 */
export function plateOf(index: number, share: Share): number | null {
  if (index < 0 || index >= share.total) return null;
  const dealt = share.each * share.plates;
  if (index >= dealt) return null;
  return index % share.plates;
}

/** How many laps of the table have been dealt by the time `index` lands. */
export function lapOf(index: number, share: Share): number {
  return Math.floor(index / share.plates);
}

/** The sentence a child would say about what just happened. */
export function shareLine(share: Share): string {
  const { total, plates, each, left } = share;
  const between = plates === 1 ? 'for 1 plate' : `between ${plates}`;
  if (total === 0) return 'Nothing to share yet';
  if (left === 0) return `${total} ${between} is ${each} each, none left over`;
  return `${total} ${between} is ${each} each, and ${left} left over`;
}

/**
 * The smallest number of extra things that would make it share out evenly —
 * the answer to "how many more do we need so nobody misses out".
 */
export function neededToLevel(share: Share): number {
  return share.left === 0 ? 0 : share.plates - share.left;
}
